import { mergeOptionColors, normalizeFieldOptions } from './fieldOptionsUtils';
import { getLinkedSubStatusEntries } from './statusFieldHierarchyUtils';
import { isSubStatusField } from './formStudioFormUtils';

function normalizeOptionKey(value) {
  return String(value || '').trim();
}

function cloneMap(map = {}) {
  return Object.fromEntries(
    Object.entries(map).map(([key, options]) => [key, normalizeFieldOptions(options)])
  );
}

export function hasPerOptionSubstatusMap(field) {
  const map = field?.substatuses_by_parent_option;
  return Boolean(map && typeof map === 'object' && Object.keys(map).length > 0);
}

export function getSubstatusOptionsForParentOption(field, parentOptionName) {
  const optionKey = normalizeOptionKey(parentOptionName);
  if (!optionKey) {
    return [];
  }

  const map = field?.substatuses_by_parent_option;
  if (map && typeof map === 'object' && Object.prototype.hasOwnProperty.call(map, optionKey)) {
    return normalizeFieldOptions(map[optionKey]);
  }

  return normalizeFieldOptions(field?.key_options);
}

export function setSubstatusOptionsForParentOption(
  field,
  parentOptionName,
  options,
  colorsPatch = null
) {
  const optionKey = normalizeOptionKey(parentOptionName);
  const normalizedOptions = normalizeFieldOptions(options);
  const nextMap = cloneMap(field?.substatuses_by_parent_option);

  nextMap[optionKey] = normalizedOptions;

  const nextField = {
    ...field,
    substatuses_by_parent_option: nextMap,
    key_options: getUnionSubstatusOptions({ ...field, substatuses_by_parent_option: nextMap }),
  };

  if (colorsPatch && typeof colorsPatch === 'object') {
    nextField.option_colors = {
      ...(field?.option_colors || {}),
      ...colorsPatch,
    };
  }

  return nextField;
}

export function getUnionSubstatusOptions(field) {
  const map = field?.substatuses_by_parent_option;
  if (map && typeof map === 'object' && Object.keys(map).length > 0) {
    const seen = new Set();
    const union = [];
    Object.values(map).forEach((options) => {
      normalizeFieldOptions(options).forEach((name) => {
        const key = name.toLowerCase();
        if (!seen.has(key)) {
          seen.add(key);
          union.push(name);
        }
      });
    });
    return union;
  }

  return normalizeFieldOptions(field?.key_options);
}

export function subStatusFieldHasConfiguredOptions(field, parentStatusOptions = []) {
  if (!isSubStatusField(field)) {
    return false;
  }

  if (hasPerOptionSubstatusMap(field)) {
    return parentStatusOptions.some(
      (optionName) => getSubstatusOptionsForParentOption(field, optionName).length > 0
    );
  }

  return normalizeFieldOptions(field?.key_options).length > 0;
}

export function buildSubstatusMapFromApiStatuses(subField, parentStatuses = []) {
  const subFieldKey = subField?.key_name;
  if (!subFieldKey) {
    return {};
  }

  const map = {};

  parentStatuses.forEach((parentStatus) => {
    const parentOptionName = normalizeOptionKey(parentStatus?.status_name);
    if (!parentOptionName) return;

    const subs = (parentStatus?.substatuses || [])
      .filter((sub) => sub?.type_of_status === subFieldKey)
      .map((sub) => normalizeOptionKey(sub?.name))
      .filter(Boolean);

    map[parentOptionName] = subs;
  });

  return map;
}

export function hydrateSubStatusFieldFromApi(subField, parentStatuses = []) {
  if (!isSubStatusField(subField)) {
    return subField;
  }

  const apiMap = buildSubstatusMapFromApiStatuses(subField, parentStatuses);
  if (!Object.keys(apiMap).length) {
    return subField;
  }

  const nextField = {
    ...subField,
    substatuses_by_parent_option: apiMap,
  };

  return {
    ...nextField,
    key_options: getUnionSubstatusOptions(nextField),
  };
}

export function renameParentOptionInSubstatusMaps(allFields, parentKey, oldName, newName) {
  const fromKey = normalizeOptionKey(oldName);
  const toKey = normalizeOptionKey(newName);
  if (!fromKey || !toKey || fromKey === toKey) {
    return allFields;
  }

  return allFields.map((field) => {
    if (!isSubStatusField(field) || field.status_parent_key !== parentKey) {
      return field;
    }

    const map = cloneMap(field.substatuses_by_parent_option);
    if (!Object.prototype.hasOwnProperty.call(map, fromKey)) {
      return field;
    }

    const movedOptions = map[fromKey];
    delete map[fromKey];
    map[toKey] = movedOptions;

    const nextColors = { ...(field.option_colors || {}) };
    if (nextColors[fromKey] && !nextColors[toKey]) {
      nextColors[toKey] = nextColors[fromKey];
      delete nextColors[fromKey];
    }

    const nextField = {
      ...field,
      substatuses_by_parent_option: map,
      option_colors: nextColors,
    };

    return {
      ...nextField,
      key_options: getUnionSubstatusOptions(nextField),
    };
  });
}

export function removeParentOptionFromSubstatusMaps(allFields, parentKey, removedName) {
  const optionKey = normalizeOptionKey(removedName);
  if (!optionKey) {
    return allFields;
  }

  return allFields.map((field) => {
    if (!isSubStatusField(field) || field.status_parent_key !== parentKey) {
      return field;
    }

    const map = cloneMap(field.substatuses_by_parent_option);
    if (!Object.prototype.hasOwnProperty.call(map, optionKey)) {
      return field;
    }

    delete map[optionKey];

    const nextField = {
      ...field,
      substatuses_by_parent_option: map,
    };

    return {
      ...nextField,
      key_options: getUnionSubstatusOptions(nextField),
    };
  });
}

export function syncSubstatusMapsWithParentOptions(allFields, parentField, previousParentKey = '') {
  const parentOptions = normalizeFieldOptions(parentField?.key_options);
  const parentKey = normalizeOptionKey(parentField?.key_name);
  const linkedEntries = getLinkedSubStatusEntries(allFields, parentField, previousParentKey);

  if (!linkedEntries.length || !parentOptions.length) {
    return allFields;
  }

  return allFields.map((field) => {
    const isLinked = linkedEntries.some(({ field: linked }) => linked === field);
    if (!isLinked) {
      return field;
    }

    const map = cloneMap(field.substatuses_by_parent_option);
    const legacyOptions = normalizeFieldOptions(field.key_options);
    const fallbackOptions = Object.keys(map).length ? [] : legacyOptions;

    parentOptions.forEach((optionName) => {
      if (!Object.prototype.hasOwnProperty.call(map, optionName)) {
        map[optionName] = [...fallbackOptions];
      }
    });

    Object.keys(map).forEach((optionName) => {
      if (!parentOptions.includes(optionName)) {
        delete map[optionName];
      }
    });

    const nextField = {
      ...field,
      substatuses_by_parent_option: map,
    };

    return {
      ...nextField,
      key_options: getUnionSubstatusOptions(nextField),
    };
  });
}

export function diffParentStatusOptions(previousOptions = [], nextOptions = []) {
  const previous = normalizeFieldOptions(previousOptions);
  const next = normalizeFieldOptions(nextOptions);
  const nextSet = new Set(next.map((item) => item.toLowerCase()));
  const previousSet = new Set(previous.map((item) => item.toLowerCase()));

  const removed = previous.filter((item) => !nextSet.has(item.toLowerCase()));
  const added = next.filter((item) => !previousSet.has(item.toLowerCase()));

  const renames = [];
  if (removed.length === 1 && added.length === 1) {
    renames.push({ from: removed[0], to: added[0] });
    return { removed: [], added: [], renames };
  }

  return { removed, added, renames: [] };
}

export function applyParentOptionChangesToFields(allFields, parentField, previousOptions = []) {
  const parentKey = normalizeOptionKey(parentField?.key_name);
  const nextOptions = normalizeFieldOptions(parentField?.key_options);
  const { removed, renames } = diffParentStatusOptions(previousOptions, nextOptions);

  let updated = syncSubstatusMapsWithParentOptions(allFields, parentField);

  renames.forEach(({ from, to }) => {
    updated = renameParentOptionInSubstatusMaps(updated, parentKey, from, to);
  });

  removed.forEach((optionName) => {
    updated = removeParentOptionFromSubstatusMaps(updated, parentKey, optionName);
  });

  return updated;
}

export function updateSubStatusFieldInCollection(allFields, fieldIndex, patch) {
  return allFields.map((field, index) => (index === fieldIndex ? { ...field, ...patch } : field));
}

export function applyPerOptionSubstatusEdit(
  allFields,
  subFieldIndex,
  parentOptionName,
  options,
  colorInfo = null
) {
  const subField = allFields[subFieldIndex];
  if (!subField) {
    return allFields;
  }

  const normalizedOptions = normalizeFieldOptions(options);
  let nextField = setSubstatusOptionsForParentOption(subField, parentOptionName, normalizedOptions);

  if (colorInfo) {
    nextField = {
      ...nextField,
      option_colors: mergeOptionColors(
        nextField.key_options,
        colorInfo,
        nextField.option_colors || {}
      ),
    };
  }

  return updateSubStatusFieldInCollection(allFields, subFieldIndex, nextField);
}
